// Componente Premium público
// Página que muestra los planes de suscripción disponibles para todos los visitantes
// Integrado con el nuevo sistema de suscripciones de Mercado Pago

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiArrowRight, FiLoader, FiUser, FiLogOut } from 'react-icons/fi';
import Button from '../components/UI/Button';
import mercadoPagoService from '../services/mercadopago_compatible';
import AmarsiLogo from '../components/Icons/AmarsiLogo';
import ThemeToggleButton from '../components/UI/ThemeToggleButton';
import { useAuth } from '../hooks/useAuth';
import { usePlans } from '../hooks/usePlans';
import { useSubscription } from '../hooks/useSubscription';
import PlanCard from '../components/subscription/PlanCard';
import SubscriptionStatus from '../components/subscription/SubscriptionStatus';
import SubscriptionFormMP from '../components/subscription/SubscriptionFormMP';
import { SUCCESS_MESSAGES } from '../constants/subscriptions';

const Premium = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, signOut } = useAuth();
  const { plans, loading: plansLoading, error: plansError } = usePlans();
  const { subscription, loading: subLoading } = useSubscription(user?.id);
  
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [notification, setNotification] = useState({ type: '', message: '' });

  /**
   * Muestra una notificación temporal
   */
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  /**
   * Maneja la suscripción a un plan
   */
  const handleSubscribe = (plan) => {
    if (!isAuthenticated) {
      // Redirigir a login con callback
      navigate(`/auth/login?redirect=/premium&plan=${plan.id}`);
      return;
    }

    setSelectedPlan(plan);
    setShowSubscriptionForm(true);
  };

  /**
   * Maneja el éxito en la suscripción
   */
  const handleSubscriptionSuccess = (result) => {
    showNotification('success', SUCCESS_MESSAGES.SUBSCRIPTION_CREATED);
    setShowSubscriptionForm(false);
    setSelectedPlan(null);
  };

  /**
   * Maneja errores en la suscripción
   */
  const handleSubscriptionError = (error) => {
    showNotification('error', error);
  };

  /**
   * Maneja la cancelación del formulario
   */
  const handleFormCancel = () => {
    setShowSubscriptionForm(false);
    setSelectedPlan(null);
  };

  /**
   * Maneja el logout
   */
  const handleLogout = async () => {
    await signOut();
    navigate('/');
  };

  /**
   * Renderiza la notificación
   */
  const renderNotification = () => {
    if (!notification.message) return null;

    const bgColor = notification.type === 'success' ? 'bg-green-500' : 'bg-red-500';
    
    return (
      <div className={`fixed top-4 right-4 z-50 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg`}>
        {notification.message}
      </div>
    );
  };

  /**
   * Renderiza el header del usuario
   */
  const renderUserHeader = () => {
    if (!isAuthenticated) {
      return (
        <Link to="/auth/login">
          <Button variant="primary" size="sm">
            Iniciar Sesión
          </Button>
        </Link>
      );
    }

    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 text-sm">
          <FiUser className="h-4 w-4" />
          <span className="text-gray-700 dark:text-gray-300">
            {user?.email}
          </span>
        </div>
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          <FiLogOut className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  /**
   * Renderiza el estado de suscripción del usuario
   */
  const renderUserSubscription = () => {
    if (!isAuthenticated || !subscription) return null;

    return (
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-6 mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
          Tu Suscripción Actualssssssssssssss
        </h2>
        <div className="flex items-center justify-between">
          <div>
            <SubscriptionStatus 
              subscription={subscription} 
              showDetails={true} 
              size="lg" 
            />
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Próxima renovación
            </p>
            <p className="font-semibold text-gray-900 dark:text-white">
              {subscription.proxima_renovacion 
                ? new Date(subscription.proxima_renovacion).toLocaleDateString('es-MX')
                : 'N/A'
              }
            </p>
          </div>
        </div>
      </div>
    );
  };

  /**
   * Renderiza el formulario de suscripción
   */
  if (showSubscriptionForm && selectedPlan) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center p-4">
        <SubscriptionFormMP
          plan={selectedPlan}
          user={user}
          onSuccess={handleSubscriptionSuccess}
          onError={handleSubscriptionError}
          onCancel={handleFormCancel}
        />
        {renderNotification()}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      {/* Header */}
      <header className="bg-white dark:bg-neutral-800 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-4">
              <AmarsiLogo width="32" height="32" />
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Amarsi</h1>
            </div>
            <nav className="flex items-center space-x-4">
              <Link 
                to="/" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Inicio
              </Link>
              <Link 
                to="/premium" 
                className="text-primary dark:text-primary font-medium"
              >
                Premium
              </Link>
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Privacidad
              </Link>

              <ThemeToggleButton />
              {renderUserHeader()}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
            Planes
            <span className="block text-primary dark:text-primary">Premium</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 max-w-3xl mx-auto">
            Elige el plan perfecto para ti. Desbloquea características exclusivas 
            y lleva tus conexiones al siguiente nivel.
          </p>
        </div>

        {/* User Subscription Status */}
        {renderUserSubscription()}

        {/* Loading State */}
        {(plansLoading || subLoading) && (
          <div className="flex justify-center items-center py-20">
            <FiLoader className="animate-spin h-8 w-8 text-primary" />
            <span className="ml-3 text-gray-600 dark:text-gray-400">
              {plansLoading ? 'Cargando planes...' : 'Verificando suscripción...'}
            </span>
          </div>
        )}

        {/* Error State */}
        {plansError && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center mb-8">
            <p className="text-red-800 dark:text-red-200">{plansError}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Reintentar
            </Button>
          </div>
        )}

        {/* Plans Grid */}
        {!plansLoading && !subLoading && !plansError && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {plans.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                userSubscription={subscription}
                onSubscribe={handleSubscribe}
                loading={subLoading}
                popular={plan.popular || false}
                recommended={plan.recomendado || false}
              />
            ))}
          </div>
        )}

        {/* FAQ Section */}
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-lg p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            Preguntas Frecuentes
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                ¿Puedo cancelar mi suscripción en cualquier momento?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Sí, puedes cancelar tu suscripción en cualquier momento desde tu cuenta. 
                Seguirás teniendo acceso hasta el final del período facturado.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                ¿Hay algún cargo oculto?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                No. El precio que ves es el precio que pagas. No hay cargos ocultos 
                ni comisiones adicionales.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                ¿Qué métodos de pago aceptan?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Aceptamos todas las tarjetas de crédito y débito principales, 
                así como pagos a través de Mercado Pago.
              </p>
            </div>
            
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                ¿Es seguro mi pago?
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Sí, todos los pagos se procesan a través de conexiones seguras 
                con encriptación SSL y cumplimos con los estándares de seguridad más estrictos.
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center bg-neutral-700 rounded-2xl p-12">
          <h2 className="text-3xl font-bold text-white mb-4">
            ¿Listo para comenzar?
          </h2>
          <p className="text-violet-100 mb-8 text-lg">
            Únete a miles de usuarios que ya están conectando con personas cerca de ellos.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {!isAuthenticated ? (
              <Link to="/auth/register">
                <Button variant="primary" size="lg">
                  Crear Cuenta Gratis
                  <FiArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            ) : (
              <Link to="/profile">
                <Button variant="primary" size="lg">
                  Ir a mi Perfil
                  <FiArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            )}
            <Link to="/">
              <Button variant="ghost-primary" size="lg">
                Conocer Más
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-neutral-800 mt-20 border-t border-gray-200 dark:border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center mb-4 md:mb-0 gap-4">
              <AmarsiLogo width='2rem' height='2rem'/>
              <span className="text-gray-900 dark:text-white font-semibold">Amarsi</span>
              <span className="text-gray-500">© {new Date().getFullYear()} Amarsi. Todos los derechos reservados.</span>
            </div>
            <div className="flex space-x-6 text-sm">
              <Link 
                to="/" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Inicio
              </Link>
              <Link 
                to="/premium" 
                className="text-primary dark:text-primary font-medium"
              >
                Premium
              </Link>
              <Link 
                to="/politicas-easi" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Políticas EASI
              </Link>
              <Link 
                to="/privacidad" 
                className="text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors"
              >
                Privacidad
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Notification */}
      {renderNotification()}
    </div>
  );
};

export default Premium;
